import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SegmentedControl from '../../components/SegmentedControl.jsx';
import { API_BASE_URL, fetchCalendar, fetchCalendarPeople, fetchDayDetails, fetchPlanningLookups, deletePlannedTask, removePlannedTaskDay, fetchTimeEntry, deleteTimeEntry } from '../../api';
import { formatMinutesToHM, formatRangeDMY, formatStatusLabel } from '../../ui/format';
import Chip from '../../ui/Chip.jsx';
import { toYMD } from './calendarUtils';
import { getPersonColor } from './peopleColors';
import { deriveProjectColor, deriveProjectColorVariant } from '../../utils/projectColors';
import toast from 'react-hot-toast';
import SharedAddWorkModal from './SharedAddWorkModal.jsx';
import TaskDetailsModal from './TaskDetailsModal.jsx';
import TimeEntryDetailsModal from '../../ui/TimeEntryDetailsModal.jsx';
import { useAppRefresh } from '../../context/AppRefreshContext';
import { getActiveTheme, isRedFocusThemeName, resolveThemeName } from '../../ui/tokens';
import AlertDialog from '../../ui/AlertDialog.jsx';

function normalizeName(s) {
  return String(s || '').trim().toLowerCase();
}

function isSeededDemoAccountName(displayName) {
  const name = normalizeName(displayName);
  if (!name) return false;
  return name === 'admin test'
    || name === 'manager test'
    || name === 'admin and manager test'
    || name === 'employee test';
}

function isAdminAccountName(displayName) {
  const name = normalizeName(displayName);
  if (!name) return false;
  return name === 'admin' || name === 'admin user';
}

const PINNED_MANAGER_FIRST_NAMES = ['vinko', 'sanela', 'andrea'];

function firstNameKey(displayName) {
  const full = normalizeName(displayName);
  return (full.split(/\s+/)[0] || full).trim();
}

function pinnedManagerIndex(displayName) {
  const first = firstNameKey(displayName);
  return PINNED_MANAGER_FIRST_NAMES.indexOf(first);
}

function isPinnedManager(displayName) {
  return pinnedManagerIndex(displayName) >= 0;
}

function sortPeoplePinnedThenAlpha(people) {
  const rows = Array.isArray(people) ? people : [];
  const cleaned = rows
    .filter(e => e && e.id != null && e.display_name && String(e.display_name).trim() && normalizeName(e.display_name) !== 'user');

  const pinned = PINNED_MANAGER_FIRST_NAMES
    .map((first) => cleaned.find(e => firstNameKey(e.display_name) === first))
    .filter(Boolean);

  const pinnedIds = new Set(pinned.map(p => String(p.id)));
  const rest = cleaned
    .filter(e => !pinnedIds.has(String(e.id)))
    .slice()
    .sort((a, b) => String(a.display_name || '').localeCompare(String(b.display_name || '')));

  return [...pinned, ...rest];
}

function ymdToDate(ymd) {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

function formatDateDMY(ymd) {
  const s = String(ymd || '').slice(0, 10);
  if (s.length !== 10) return '—';
  const yyyy = s.slice(0, 4);
  const mm = s.slice(5, 7);
  const dd = s.slice(8, 10);
  if (!yyyy || !mm || !dd) return '—';
  return `${dd}/${mm}/${yyyy}`;
}

function clampYmdRange(from, to) {
  if (!from || !to) return { from: '', to: '' };
  return from <= to ? { from, to } : { from: to, to: from };
}

function formatDayLabel(ymd) {
  const d = ymdToDate(ymd);
  if (!d) return ymd;
  const wd = d.toLocaleDateString(undefined, { weekday: 'short' });
  return `${wd} ${d.getDate()}`;
}

function isWeekendYmd(ymd) {
  const d = ymdToDate(ymd);
  if (!d) return false;
  const g = d.getDay();
  return g === 0 || g === 6;
}

function distributeMinutesOverRange(totalMinutes, startYmd, dueYmd, includeWeekends) {
  const total = Number(totalMinutes || 0);
  if (!Number.isFinite(total) || total <= 0) return new Map();
  if (!dueYmd || !/^\d{4}-\d{2}-\d{2}$/.test(dueYmd)) return new Map();

  // If there is no start date, allocate all minutes to due_date (unless it's a weekend and weekends are excluded).
  if (!startYmd || !/^\d{4}-\d{2}-\d{2}$/.test(startYmd)) {
    if (includeWeekends || !isWeekendYmd(dueYmd)) {
      return new Map([[dueYmd, total]]);
    }
    return new Map();
  }

  const start = ymdToDate(startYmd);
  const due = ymdToDate(dueYmd);
  if (!start || !due) return new Map();

  const days = [];
  for (let d = new Date(start); d <= due; d.setDate(d.getDate() + 1)) {
    const ymd = toYMD(d);
    if (!includeWeekends && isWeekendYmd(ymd)) continue;
    days.push(ymd);
  }

  // No eligible days in range (e.g. weekend-only window with include_weekends=false).
  // Mirror backend behavior: place on due_date so the item stays visible.
  if (days.length === 0) return new Map([[dueYmd, total]]);

  const perDay = Math.floor(total / days.length);
  let remainder = total - perDay * days.length;
  const out = new Map();
  for (const ymd of days) {
    const add = perDay + (remainder > 0 ? 1 : 0);
    if (remainder > 0) remainder -= 1;
    out.set(ymd, add);
  }
  return out;
}

export default function Calendar2({
  today = new Date(),
  storageNamespace = 'calendar',
  fixedProjectId = null,
  hideProjectFilter = false,
  enableAssignWorkCta = false,
  onWorkCreated = null,
  onWindowChange = null,
}) {
  const nav = useNavigate();
  const { refreshKey: appRefreshKey, bumpRefresh } = useAppRefresh();

  const themeName = resolveThemeName();
  const theme = getActiveTheme();
  const isRedFocus = isRedFocusThemeName(themeName);
  const isRed2 = String(themeName) === 'red2';
  const warmTheme = isRedFocus ? theme : null;

  const LABEL_COL_PX = 360;
  const DAY_COL_PX = 28;

  const [me, setMe] = useState(null);

  const [view, setView] = useState('week'); // 'month' | 'week'
  const [subtab, setSubtab] = useState('tasks'); // 'tasks' | 'people'

  const [baseDate, setBaseDate] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [projectId, setProjectId] = useState(fixedProjectId ? String(fixedProjectId) : '');
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState([]);

  const [managers, setManagers] = useState([]); // [{id, display_name}]
  const [managerFilter, setManagerFilter] = useState('me'); // 'me' | 'all' | managerId

  const [visibleEmployees, setVisibleEmployees] = useState([]);
  const [hiddenEmpIds, setHiddenEmpIds] = useState([]); // stored as strings
  const [peopleFilterInitialized, setPeopleFilterInitialized] = useState(false);
  const [legacySelectedEmpIds, setLegacySelectedEmpIds] = useState(null);
  const [dayTotals, setDayTotals] = useState({});
  const [dayDetails, setDayDetails] = useState(null);

  const DAY_LIMIT_MINUTES = 8 * 60;
  const WEEK_LIMIT_MINUTES = 40 * 60;

  const getPeopleDayInfo = (ymd, uid) => {
    const info = (dayTotals?.[ymd] || {})?.[uid];
    const planned = Number((plannedByDayUser?.[ymd] || {})?.[String(uid)] || 0);
    const loggedAssigned = Number(info?.assigned_minutes || 0);
    const other = Number(info?.other_minutes || 0);
    const total = Number(info?.total_minutes || 0);

    // Prefer logged minutes when they exist; otherwise use planned as a proxy.
    // This is ONLY for visual alerts; planning/distribution logic is unchanged.
    const hasLogged = info != null && Number.isFinite(total) && total > 0;
    const minutesForAlerts = hasLogged ? total : planned;
    const over8h = minutesForAlerts > DAY_LIMIT_MINUTES;

    const showMinutes = planned > 0 || total > 0 || other > 0;
    const primaryMinutes = total > 0 ? total : planned;

    return {
      planned,
      loggedAssigned,
      other,
      total,
      showMinutes,
      primaryMinutes,
      minutesForAlerts,
      over8h,
    };
  };

  const getWeekStartYmd = (ymd) => {
    const d = ymdToDate(ymd);
    if (!d) return null;
    const dow = d.getDay();
    const offset = (dow === 0 ? -6 : 1 - dow); // Monday start
    const m = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    m.setDate(m.getDate() + offset);
    return toYMD(m);
  };

  const getWeekTotalMinutesForUser = (weekStartYmd, uid) => {
    if (!weekStartYmd) return 0;
    const start = ymdToDate(weekStartYmd);
    if (!start) return 0;
    let sum = 0;
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const ymd = toYMD(d);
      sum += Number(getPeopleDayInfo(ymd, uid).minutesForAlerts || 0);
    }
    return sum;
  };

  const [taskDetailsOpen, setTaskDetailsOpen] = useState(false);
  const [taskDetailsAssignment, setTaskDetailsAssignment] = useState(null);

  const [timeEntryDetailsOpen, setTimeEntryDetailsOpen] = useState(false);
  const [timeEntryDetailsId, setTimeEntryDetailsId] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, assignment: null, busy: false });
  const [removeDayConfirm, setRemoveDayConfirm] = useState({ open: false, taskId: null, ymd: null, userId: null, busy: false });

  const [workEditAssignmentId, setWorkEditAssignmentId] = useState(null);
  const [drawerReloadKey, setDrawerReloadKey] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [refreshKey, setRefreshKey] = useState(0);

  const canManageTasks = me?.role === 'manager' || me?.role === 'admin' || me?.role === 'junior_manager';

  const openDeleteConfirm = (assignment) => {
    setDeleteConfirm({ open: true, assignment: assignment || null, busy: false });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({ open: false, assignment: null, busy: false });
  };

  const openRemoveDayConfirm = ({ taskId, ymd, userId }) => {
    setRemoveDayConfirm({ open: true, taskId: taskId || null, ymd: ymd || null, userId: userId || null, busy: false });
  };

  const closeRemoveDayConfirm = () => {
    setRemoveDayConfirm({ open: false, taskId: null, ymd: null, userId: null, busy: false });
  };

  const confirmDelete = async () => {
    const a = deleteConfirm.assignment;
    const taskId = a?.id || a?.assignment_id || null;
    if (!taskId) {
      toast.error('Delete not available');
      closeDeleteConfirm();
      return;
    }

    setDeleteConfirm(prev => ({ ...prev, busy: true }));
    try {
      await deletePlannedTask(taskId);
      toast.success('Task deleted');
      closeDeleteConfirm();

      if (String(workEditAssignmentId || '') === String(taskId)) setWorkEditAssignmentId(null);
      if (taskDetailsOpen && String(taskDetailsAssignment?.id || '') === String(taskId)) {
        setTaskDetailsOpen(false);
        setTaskDetailsAssignment(null);
      }

      setDrawerReloadKey(k => k + 1);
      setRefreshKey(k => k + 1);
    } catch (err) {
      const status = err?.status;
      const msg = String(err?.message || '');
      if (status === 409 && msg === 'TASK_HAS_LOGGED_TIME') {
        toast.error('Deletion blocked: this task has logged hours.');
      } else {
        toast.error(err?.message || 'Failed to delete');
      }
      setDeleteConfirm(prev => ({ ...prev, busy: false }));
    }
  };

  const confirmRemoveDay = async () => {
    const taskId = removeDayConfirm.taskId;
    const ymd = removeDayConfirm.ymd;
    const userId = removeDayConfirm.userId;
    if (!taskId || !ymd) {
      toast.error('Remove day not available');
      closeRemoveDayConfirm();
      return;
    }

    const viewerRole = String(me?.role || '').trim().toLowerCase();
    const isManagerAdmin = viewerRole === 'manager' || viewerRole === 'admin';
    if (isManagerAdmin && !userId) {
      toast.error('Remove day not available');
      closeRemoveDayConfirm();
      return;
    }

    setRemoveDayConfirm(prev => ({ ...prev, busy: true }));
    try {
      await removePlannedTaskDay(taskId, ymd, userId ? { userId } : {});
      toast.success('Day removed');
      closeRemoveDayConfirm();
      setDrawerReloadKey(k => k + 1);
      setRefreshKey(k => k + 1);
    } catch (err) {
      const status = err?.status;
      const msg = String(err?.message || '');
      if (status === 409 && msg === 'DAY_HAS_LOGGED_TIME') {
        toast.error('Can’t remove: hours already logged for this day.');
      } else {
        toast.error(err?.message || 'Failed to remove day');
      }
      setRemoveDayConfirm(prev => ({ ...prev, busy: false }));
    }
  };

  useEffect(() => {
    setRefreshKey(k => k + 1);
  }, [appRefreshKey]);

  // Modal open-state encoding:
  // - undefined: closed
  // - null: open in global mode (modal shows Client + Project dropdowns)
  // - object: open scoped to a fixed project
  const [workProject, setWorkProject] = useState(undefined);
  const [workInitialAssigneeIds, setWorkInitialAssigneeIds] = useState([]);
  const [workPrefill, setWorkPrefill] = useState(null); // { jobId, taskId, plannedHours, startDate, dueDate }
  const [workMode, setWorkMode] = useState('create'); // create | edit
  const [workInitialAssignment, setWorkInitialAssignment] = useState(null);

  const hiddenPeopleKey = useMemo(() => {
    if (!me?.id) return null;
    return `calendar_people_hidden::${me.id}`;
  }, [me?.id]);

  const legacySelectionKey = useMemo(() => {
    if (!me?.id) return null;
    return `calendar_people_selection:${storageNamespace}:${me.id}`;
  }, [me?.id, storageNamespace]);

  const filterKey = useMemo(() => {
    if (!me?.id) return null;
    return `calendar_filters:${storageNamespace}:${me.id}`;
  }, [me?.id, storageNamespace]);

  const sanitizedVisibleEmployees = useMemo(() => {
    const canSeeSeeded = normalizeName(me?.email) === 'admin@example.com';
    const base = sortPeoplePinnedThenAlpha(visibleEmployees);
    const withoutAdmin = base.filter(e => !isAdminAccountName(e?.display_name));
    if (canSeeSeeded) return withoutAdmin;
    return withoutAdmin.filter(e => !isSeededDemoAccountName(e?.display_name));
  }, [visibleEmployees, me?.email]);

  const displaySelectedEmpIds = useMemo(() => {
    const hidden = new Set((Array.isArray(hiddenEmpIds) ? hiddenEmpIds : []).map(String));
    return (Array.isArray(sanitizedVisibleEmployees) ? sanitizedVisibleEmployees : [])
      .map(e => e.id)
      .filter(id => !hidden.has(String(id)));
  }, [hiddenEmpIds, sanitizedVisibleEmployees]);

  // Load me
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled) setMe(j?.user || null);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist view/subtab
  useEffect(() => {
    try {
      const raw = localStorage.getItem(`${storageNamespace}.calendar2`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.view === 'month' || parsed?.view === 'week') setView(parsed.view);
      // Backward-compat: map legacy 'timeline' to new 'week'
      if (parsed?.view === 'timeline') setView('week');
      if (parsed?.subtab === 'people' || parsed?.subtab === 'tasks') setSubtab(parsed.subtab);
      if (parsed?.subtab === 'projects') setSubtab('tasks');
    } catch {}
  }, [storageNamespace]);
  useEffect(() => {
    try { localStorage.setItem(`${storageNamespace}.calendar2`, JSON.stringify({ view, subtab })); } catch {}
  }, [storageNamespace, view, subtab]);

  // Persist filters
  useEffect(() => {
    // When scoped to a fixed project, manager ownership filtering is not meaningful
    // and also causes empty views if the project belongs to a different manager.
    // Avoid applying persisted manager filters in this mode.
    if (fixedProjectId) return;
    if (!filterKey) return;
    try {
      const raw = localStorage.getItem(filterKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed?.managerFilter) setManagerFilter(parsed.managerFilter);
    } catch {}
  }, [filterKey]);

  useEffect(() => {
    if (fixedProjectId) return;
    if (!filterKey) return;
    try {
      localStorage.setItem(filterKey, JSON.stringify({ managerFilter }));
    } catch {}
  }, [filterKey, managerFilter]);

  // Load managers list for filter dropdown
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const lk = await fetchPlanningLookups({ redirectOn401: false });
        if (!cancelled) setManagers(Array.isArray(lk?.managers) ? lk.managers : []);
      } catch {
        if (!cancelled) setManagers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // People selection persistence (store HIDDEN ids per manager, so new employees are visible by default)
  useEffect(() => {
    if (!hiddenPeopleKey) return;
    setPeopleFilterInitialized(false);
    setLegacySelectedEmpIds(null);
    try {
      const raw = localStorage.getItem(hiddenPeopleKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setHiddenEmpIds(parsed.map(String));
          setPeopleFilterInitialized(true);
          return;
        }
      }
    } catch {}

    // Legacy migration support: older builds stored SELECTED ids.
    try {
      const rawLegacy = legacySelectionKey ? localStorage.getItem(legacySelectionKey) : null;
      if (rawLegacy) {
        const parsedLegacy = JSON.parse(rawLegacy);
        if (Array.isArray(parsedLegacy)) {
          setLegacySelectedEmpIds(parsedLegacy.map(String));
        }
      }
    } catch {}

    setHiddenEmpIds([]);
  }, [hiddenPeopleKey, legacySelectionKey]);

  useEffect(() => {
    if (!hiddenPeopleKey || !peopleFilterInitialized) return;
    try { localStorage.setItem(hiddenPeopleKey, JSON.stringify(hiddenEmpIds)); } catch {}
  }, [hiddenPeopleKey, peopleFilterInitialized, hiddenEmpIds]);

  // Window computation
  const computedRange = useMemo(() => {
    // Custom overrides presets.
    if (customFrom && customTo) {
      const { from, to } = clampYmdRange(customFrom, customTo);
      const fromD = ymdToDate(from);
      const toD = ymdToDate(to);
      return {
        fromYmd: from,
        toYmd: to,
        label: fromD && toD ? formatRangeDMY(fromD, toD) : `${from} – ${to}`,
      };
    }

    const d = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
    if (view === 'month') {
      const from = new Date(d.getFullYear(), d.getMonth(), 1);
      const to = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const fromYmd = toYMD(from);
      const toYmd = toYMD(to);
      return {
        fromYmd,
        toYmd,
        label: `${from.toLocaleString(undefined, { month: 'short' })} ${from.getFullYear()}`,
      };
    }

    // Week: Monday..Sunday
    const tmp = new Date(d);
    const dow = tmp.getDay();
    const offset = (dow === 0 ? -6 : 1 - dow);
    tmp.setDate(tmp.getDate() + offset);
    const from = new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate());
    const to = new Date(from);
    to.setDate(to.getDate() + 6);
    const fromYmd = toYMD(from);
    const toYmd = toYMD(to);
    return { fromYmd, toYmd, label: formatRangeDMY(from, to) };
  }, [baseDate, customFrom, customTo, view]);

  const fromYmd = computedRange.fromYmd;
  const toYmd = computedRange.toYmd;
  const windowLabel = computedRange.label;

  // Notify parent of displayed window changes (used by ManagerDashboard header)
  useEffect(() => {
    if (!onWindowChange) return;
    if (!fromYmd || !toYmd) return;
    try { onWindowChange({ from: fromYmd, to: toYmd }); } catch {}
  }, [fromYmd, toYmd, onWindowChange]);

  // Keep projectId locked when fixed
  useEffect(() => {
    if (!fixedProjectId) return;
    setProjectId(String(fixedProjectId));
    // In fixed-project mode, always show the project's data regardless of who created it.
    // (Manager filter would otherwise hide everything for projects owned by other managers.)
    setManagerFilter('all');
  }, [fixedProjectId]);

  // Fetch calendar
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!fromYmd || !toYmd) return;
      setLoading(true);
      setError('');
      try {
        const cal = await fetchCalendar(fromYmd, toYmd, projectId || null);
        if (!cancelled && cal) {
          setProjects(Array.isArray(cal.projects) ? cal.projects : []);
          setAssignments(Array.isArray(cal.assignments) ? cal.assignments : []);
        }

        if (!cancelled && subtab === 'people') {
          const ppl = await fetchCalendarPeople(fromYmd, toYmd, null, {
            projectId: projectId || null,
            managerFilter,
          });
          setVisibleEmployees(Array.isArray(ppl?.visibleEmployees) ? ppl.visibleEmployees : []);
          setDayTotals(ppl?.dayTotals || {});
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load calendar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fromYmd, toYmd, projectId, subtab, refreshKey, managerFilter]);

  // Initialize/migrate/sanitize hidden ids once we have a visible list.
  useEffect(() => {
    if (subtab !== 'people') return;
    if (!hiddenPeopleKey) return;
    if (!sanitizedVisibleEmployees.length) return;

    const visibleOrdered = sanitizedVisibleEmployees.map(e => String(e.id));
    const visibleSet = new Set(visibleOrdered);

    if (!peopleFilterInitialized) {
      if (Array.isArray(legacySelectedEmpIds)) {
        const legacySelectedSet = new Set(legacySelectedEmpIds.filter(id => visibleSet.has(String(id))));
        const nextHidden = visibleOrdered.filter(id => !legacySelectedSet.has(String(id)));
        setHiddenEmpIds(nextHidden);
        setPeopleFilterInitialized(true);
        try { localStorage.setItem(hiddenPeopleKey, JSON.stringify(nextHidden)); } catch {}
        if (legacySelectionKey) {
          try { localStorage.removeItem(legacySelectionKey); } catch {}
        }
        return;
      }

      // Default: select all => hide none
      setPeopleFilterInitialized(true);
      return;
    }

    // Sanitize hidden ids against currently visible ids.
    const prev = Array.isArray(hiddenEmpIds) ? hiddenEmpIds.map(String) : [];
    const next = prev.filter(id => visibleSet.has(String(id)));
    if (next.length !== prev.length) {
      setHiddenEmpIds(next);
    }
  }, [subtab, hiddenPeopleKey, sanitizedVisibleEmployees, peopleFilterInitialized, legacySelectedEmpIds, legacySelectionKey, hiddenEmpIds]);

  const projectOptions = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    const filtered = list.filter(p => {
      if (!p) return false;
      if (managerFilter === 'all') return true;
      if (managerFilter === 'me') return !me?.id || String(p.created_by_user_id || '') === String(me.id);
      return String(p.created_by_user_id || '') === String(managerFilter);
    });
    return filtered.slice().sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [projects, managerFilter, me?.id]);

  const filteredAssignments = useMemo(() => {
    const list = Array.isArray(assignments) ? assignments : [];
    const byOwner = list.filter(a => {
      if (!a) return false;
      const proj = (Array.isArray(projects) ? projects : []).find(p => String(p.id) === String(a.project_id));
      if (!proj) return managerFilter === 'all';
      if (managerFilter === 'all') return true;
      if (managerFilter === 'me') return !me?.id || String(proj.created_by_user_id || '') === String(me.id);
      return String(proj.created_by_user_id || '') === String(managerFilter);
    });
    return byOwner;
  }, [assignments, projects, managerFilter, me?.id]);

  // People tab planned minutes must respect include_weekends per assignment.
  // Source of truth: /api/calendar assignments (include_weekends + per-assignee planned_minutes).
  // This intentionally overrides planned_minutes returned by /api/manager/calendar-people, without changing Task calendar.
  const plannedByDayUser = useMemo(() => {
    const out = {};
    if (!fromYmd || !toYmd) return out;

    const selected = new Set((Array.isArray(displaySelectedEmpIds) ? displaySelectedEmpIds : []).map(String));
    if (selected.size === 0) return out;

    const add = (ymd, userId, minutes) => {
      if (!ymd || ymd < fromYmd || ymd > toYmd) return;
      const uid = String(userId);
      if (!selected.has(uid)) return;
      const mins = Number(minutes || 0);
      if (!(mins > 0)) return;
      if (!out[ymd]) out[ymd] = {};
      out[ymd][uid] = (Number(out[ymd][uid] || 0) || 0) + mins;
    };

    for (const a of (Array.isArray(filteredAssignments) ? filteredAssignments : [])) {
      if (!a?.due_date) continue;

      const includeWeekends = !!a.include_weekends;
      const assignees = Array.isArray(a.assignees) ? a.assignees : [];
      if (assignees.length === 0) continue;

      for (const u of assignees) {
        const userId = u?.user_id;
        if (!userId) continue;
        if (!selected.has(String(userId))) continue;

        const totalPlannedMinutes = (u.planned_minutes != null) ? Number(u.planned_minutes || 0) : null;
        if (!(Number.isFinite(totalPlannedMinutes) && totalPlannedMinutes > 0)) continue;

        // Prefer per-assignee daily_plan from the calendar API when available.
        // This respects include_weekends AND single-day removals even on multi-assignee tasks.
        if (Array.isArray(u.daily_plan)) {
          for (const row of u.daily_plan) {
            const ymd = row?.date;
            if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) continue;
            add(ymd, userId, Number(row?.minutes || 0));
          }
          continue;
        }

        const dist = distributeMinutesOverRange(totalPlannedMinutes, a.start_date, a.due_date, includeWeekends);
        for (const [ymd, mins] of dist.entries()) {
          add(ymd, userId, mins);
        }
      }
    }

    return out;
  }, [filteredAssignments, displaySelectedEmpIds, fromYmd, toYmd]);

  const legendProjects = useMemo(() => {
    const filtered = projectId ? projectOptions.filter(p => String(p.id) === String(projectId)) : projectOptions;
    return filtered;
  }, [projectOptions, projectId]);

  const projectColorById = useMemo(() => {
    const map = new Map();
    (Array.isArray(projects) ? projects : []).forEach(p => {
      if (!p?.id) return;
      const shade = (p.color_variant != null)
        ? deriveProjectColorVariant(p.client_color, p.id, p.color_variant)
        : deriveProjectColor(p.client_color, p.id);
      map.set(String(p.id), shade);
    });
    return map;
  }, [projects]);

  function getProjectColorForId(pid) {
    if (pid == null) return deriveProjectColor(null, pid);
    return projectColorById.get(String(pid)) || deriveProjectColor(null, pid);
  }

  const assigneeLabels = (a) => {
    const list = Array.isArray(a?.assignees) ? a.assignees : [];
    return list
      .map(x => String(x?.display_name || '').trim())
      .filter(Boolean);
  };

  const assigneeSummary = (a) => {
    const labels = assigneeLabels(a);
    if (!labels.length) return String(a?.created_by_name || '').trim() || 'Unassigned';
    const shown = labels.slice(0, 2);
    const more = Math.max(0, labels.length - shown.length);
    return more > 0 ? `${shown.join(', ')} +${more}` : shown.join(', ');
  };

  const tasksByDay = useMemo(() => {
    const map = {};
    (Array.isArray(filteredAssignments) ? filteredAssignments : []).forEach(a => {
      const plan = Array.isArray(a?.daily_plan) ? a.daily_plan : null;
      if (plan && plan.length) {
        let hasPositiveSlice = false;
        for (const row of plan) {
          const ymd = row?.date;
          const minutes = Number(row?.minutes || 0);
          if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) continue;
          if (!(minutes > 0)) continue;
          hasPositiveSlice = true;
          if (!map[ymd]) map[ymd] = [];
          map[ymd].push({ ...a, _slice_date: ymd, _slice_minutes: minutes });
        }
        // If the plan exists but all slices are 0 minutes (common for 0h / unassigned work),
        // fall through to place a single item on due_date so it stays visible.
        if (hasPositiveSlice) return;
      }

      const ymd = a?.due_date;
      if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return;
      if (!map[ymd]) map[ymd] = [];
      map[ymd].push({ ...a, _slice_date: ymd, _slice_minutes: null });
    });
    Object.keys(map).forEach(ymd => {
      map[ymd].sort((x, y) => {
        const px = String(x?.project_name || '').localeCompare(String(y?.project_name || ''));
        if (px !== 0) return px;
        const jx = String(x?.job_name || '').localeCompare(String(y?.job_name || ''));
        if (jx !== 0) return jx;
        return String(x?.task_name || '').localeCompare(String(y?.task_name || ''));
      });
    });
    return map;
  }, [filteredAssignments]);

  function openAssignForTask(a) {
    try {
      setWorkMode('create');
      setWorkInitialAssignment(null);
      const pid = a?.project_id;
      if (!pid) {
        toast.error('Task has no project');
        return;
      }

      const proj = (Array.isArray(projects) ? projects : []).find(p => String(p.id) === String(pid))
        || (Array.isArray(projectOptions) ? projectOptions : []).find(p => String(p.id) === String(pid));

      const projLabel = proj
        ? { id: proj.id, name: proj.name, client_name: proj.client_name }
        : { id: pid, name: a?.project_name || `Project ${pid}`, client_name: a?.client_name || null };

      setWorkProject(projLabel);
      setWorkInitialAssigneeIds((Array.isArray(a?.assignees) ? a.assignees : []).map(x => x.user_id).filter(Boolean));
      setWorkPrefill({
        jobId: a?.job_id || null,
        taskId: a?.task_id || null,
        plannedHours: a?.planned_hours ?? null,
        startDate: a?.start_date || a?.due_date || null,
        dueDate: a?.due_date || null,
      });
    } catch (err) {
      console.error('openAssignForTask error', err);
      toast.error('Failed to open Assign Work');
    }
  }

  function openAssignForDate(ymd, e = null) {
    try {
      if (e?.stopPropagation) e.stopPropagation();
      if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return;

      setWorkMode('create');
      setWorkInitialAssignment(null);

      const selectedProjectObj = projectId
        ? (Array.isArray(projectOptions) ? projectOptions : []).find(p => String(p.id) === String(projectId))
        : null;

      if (!selectedProjectObj) {
        toast.error('Select a project first');
        return;
      }

      setWorkProject({ id: selectedProjectObj.id, name: selectedProjectObj.name, client_name: selectedProjectObj.client_name });
      setWorkInitialAssigneeIds([]);
      setWorkPrefill({
        jobId: null,
        taskId: null,
        plannedHours: null,
        startDate: ymd,
        dueDate: ymd,
      });
    } catch (err) {
      console.error('openAssignForDate error', err);
      toast.error('Failed to open Assign Work');
    }
  }

  function openTaskDetailsForTask(a) {
    setTaskDetailsAssignment(a || null);
    setTaskDetailsOpen(true);
  }

  function applyQuickRange(key) {
    setCustomFrom('');
    setCustomTo('');

    const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const nextBase = new Date(baseDate);

    if (view === 'month') {
      if (key === 'this') {
        nextBase.setFullYear(now.getFullYear(), now.getMonth(), now.getDate());
      } else if (key === 'last') {
        nextBase.setFullYear(now.getFullYear(), now.getMonth() - 1, now.getDate());
      } else if (key === 'next') {
        nextBase.setFullYear(now.getFullYear(), now.getMonth() + 1, now.getDate());
      }
      setBaseDate(nextBase);
      return;
    }

    // timeline: weeks
    if (key === 'this') {
      setBaseDate(now);
    } else if (key === 'last') {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      setBaseDate(d);
    } else if (key === 'next') {
      const d = new Date(now);
      d.setDate(d.getDate() + 7);
      setBaseDate(d);
    }
  }

  function stepRange(dir) {
    setCustomFrom('');
    setCustomTo('');
    const nd = new Date(baseDate);
    if (view === 'month') nd.setMonth(nd.getMonth() + dir);
    else nd.setDate(nd.getDate() + (7 * dir));
    setBaseDate(nd);
  }

  function MonthLegend() {
    if (view !== 'month') return null;
    if (subtab !== 'projects') return null;
    if (!legendProjects.length) return null;

    const max = 10;
    const shown = legendProjects.slice(0, max);
    const more = Math.max(0, legendProjects.length - shown.length);

    return (
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {shown.map(p => (
          <div key={`legend-${p.id}`} className="flex items-center gap-2 text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/40">
            <span className="inline-block w-3 h-3 rounded" style={{ background: getProjectColorForId(p.id) }} />
            <span className="truncate max-w-[220px]">{p.name}</span>
          </div>
        ))}
        {more > 0 && (
          <div className="text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300">
            +{more} more
          </div>
        )}
      </div>
    );
  }

  function MonthView() {
    if (!fromYmd || !toYmd) return <div className="text-xs text-gray-600 dark:text-gray-400">Loading…</div>;

    const start = new Date(fromYmd + 'T00:00:00');
    const first = new Date(start);
    const dow = first.getDay();
    const offset = (dow === 0 ? -6 : 1 - dow);
    first.setDate(first.getDate() + offset);

    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(first);
      d.setDate(first.getDate() + i);
      const ymd = toYMD(d);
      days.push({ date: d, ymd });
    }

    const inMonth = (d) => d.getMonth() === start.getMonth();
    const isWeekend = (d) => {
      const g = d.getDay();
      return g === 0 || g === 6;
    };

    const overlapsProject = (p, ymd) => {
      if (!p?.start_date || !p?.end_date) return false;
      return p.start_date <= ymd && p.end_date >= ymd;
    };

    const projectsForCell = (ymd) => {
      const pool = projectId ? projectOptions.filter(p => String(p.id) === String(projectId)) : projectOptions;
      return pool.filter(p => overlapsProject(p, ymd));
    };

    if (subtab === 'projects') {
      const weeks = [];
      for (let w = 0; w < 6; w++) {
        weeks.push(days.slice(w * 7, (w + 1) * 7));
      }

      const pool = projectId ? projectOptions.filter(p => String(p.id) === String(projectId)) : projectOptions;
      const laneLimit = 6;

      function clipWithinWeek(startYmd, endYmd, weekStartYmd, weekEndYmd, weekDays) {
        if (!startYmd || !endYmd) return null;
        const cs = startYmd < weekStartYmd ? weekStartYmd : startYmd;
        const ce = endYmd > weekEndYmd ? weekEndYmd : endYmd;
        if (cs > ce) return null;
        const si = Math.max(0, Math.min(6, (Array.isArray(weekDays) ? weekDays : []).findIndex(c => c.ymd === cs)));
        const ei = Math.max(0, Math.min(6, (Array.isArray(weekDays) ? weekDays : []).findIndex(c => c.ymd === ce)));
        const leftPct = (si / 7) * 100;
        const widthPct = Math.max(2, ((ei - si + 1) / 7) * 100);
        return { leftPct, widthPct };
      }

      return (
        <div className="space-y-3">
          {weeks.map((week, wi) => {
            const weekStartYmd = week[0]?.ymd;
            const weekEndYmd = week[6]?.ymd;
            const weekInMonth = week.some(c => inMonth(c.date));
            if (!weekInMonth) return null;

            const weekProjects = pool
              .map(p => ({
                p,
                clip: clipWithinWeek(p.start_date, p.end_date, weekStartYmd, weekEndYmd, week),
              }))
              .filter(r => r.clip);

            const visible = weekProjects.slice(0, laneLimit);
            const hiddenCount = Math.max(0, weekProjects.length - visible.length);

            return (
              <div
                key={`wk-${wi}`}
                className="rounded border border-gray-200 dark:border-gray-800 bg-white/60 dark:bg-gray-900/40 p-3"
                style={isRed2 ? { background: theme.surface2 || '#fff1f2' } : undefined}
              >
                <div className="grid grid-cols-7 gap-0 text-[11px] mb-2">
                  {week.map(c => (
                    <div
                      key={`wk-${wi}-d-${c.ymd}`}
                      className={`px-1 py-0.5 ${isWeekend(c.date) ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
                      title={c.ymd}
                    >
                      <span className={`${inMonth(c.date) ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'}`}>{formatDayLabel(c.ymd)}</span>
                    </div>
                  ))}
                </div>

                <div className="relative" style={{ height: `${Math.max(1, visible.length) * 22}px` }}>
                  {visible.map(({ p, clip }, li) => (
                    <button
                      key={`lane-${wi}-${p.id}`}
                      type="button"
                      onClick={() => nav(`/projects/${p.id}`)}
                      title={`${p.name} • ${p.client_name}`}
                      className="absolute left-0 right-0 rounded hover:brightness-110"
                      style={{
                        top: li * 22,
                        height: 18,
                        background: 'transparent',
                      }}
                    >
                      <div
                        className="absolute rounded flex items-center px-2"
                        style={{
                          left: `${clip.leftPct}%`,
                          width: `${clip.widthPct}%`,
                          height: 18,
                          background: getProjectColorForId(p.id),
                          boxShadow: '0 1px 0 rgba(0,0,0,0.25)',
                        }}
                      >
                        <span className="text-[11px] font-semibold truncate text-white" style={{ textShadow: '0 1px 1px rgba(0,0,0,0.35)' }}>{p.name}</span>
                      </div>
                    </button>
                  ))}

                  {hiddenCount > 0 && (
                    <div className="absolute right-0 bottom-0 text-[11px] px-2 py-1 rounded bg-gray-200/70 dark:bg-gray-800/70 text-gray-800 dark:text-gray-200">
                      +{hiddenCount}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    if (subtab === 'people') {
      // People month grid
      const weekStartByYmd = new Map();
      for (let w = 0; w < 6; w++) {
        const ws = days[w * 7]?.ymd;
        if (!ws) continue;
        for (let i = 0; i < 7; i++) {
          const ymd = days[w * 7 + i]?.ymd;
          if (ymd) weekStartByYmd.set(ymd, ws);
        }
      }

      return (
      <div
        className="grid grid-cols-7 gap-px bg-gray-300 dark:bg-gray-700"
        style={isRed2 ? { background: theme.surface2 || '#fff1f2' } : undefined}
      >
        {days.map((cell, idx) => (
          <div
            key={idx}
            className={`min-h-[72px] bg-white dark:bg-gray-900 p-2 ${isWeekend(cell.date) ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
            style={isRed2 ? { background: theme.surface2 || '#fff1f2' } : undefined}
          >
            <div className={`text-xs ${inMonth(cell.date) ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}>{formatDayLabel(cell.ymd)}</div>

            <div className="mt-1 space-y-1">
              {displaySelectedEmpIds.length > 0 && (
                <div className="mt-1 space-y-1">
                  {displaySelectedEmpIds.map(uid => {
                    const info = getPeopleDayInfo(cell.ymd, uid);
                    const planned = info.planned;
                    const loggedAssigned = info.loggedAssigned;
                    const other = info.other;
                    const total = info.total;
                    const over8h = !!info.over8h;
                    const emp = sanitizedVisibleEmployees.find(e => String(e.id) === String(uid));
                    const baseColor = getPersonColor(emp?.display_name);
                    const isEmpty = planned <= 0 && total <= 0;
                    const bg = isEmpty ? null : baseColor;
                    const text = isEmpty ? null : '#111827';
                    const showMinutes = info.showMinutes;
                    const primaryMinutes = info.primaryMinutes;

                    const weekStart = weekStartByYmd.get(cell.ymd) || getWeekStartYmd(cell.ymd);
                    const isWeekStartCell = !!weekStart && cell.ymd === weekStart;
                    const weekTotal = isWeekStartCell ? getWeekTotalMinutesForUser(weekStart, uid) : 0;
                    const over40hWeek = isWeekStartCell && weekTotal > WEEK_LIMIT_MINUTES;

                    return (
                      <button
                        key={`${cell.ymd}-${uid}`}
                        type="button"
                        className={`w-full text-[10px] px-2 py-1 rounded text-left ${isEmpty ? 'bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-300/80 dark:hover:bg-gray-700/80' : `hover:brightness-110 ${over8h ? 'ring-1 ring-red-600' : ''}`}`}
                        style={isEmpty ? undefined : { background: bg, color: text }}
                        title={showMinutes
                          ? `${emp?.display_name || 'User'} • Planned ${formatMinutesToHM(planned)} • Logged ${formatMinutesToHM(total)} (P ${formatMinutesToHM(loggedAssigned)} | O ${formatMinutesToHM(other)})`
                          : `${emp?.display_name || 'User'}`}
                        onClick={() => setDayDetails({ date: cell.ymd, user_id: uid })}
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate max-w-[70%]">{emp?.display_name || 'User'}</span>
                          <span className="flex items-center gap-1">
                            {over8h && !isEmpty && (
                              <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-semibold">&gt;8h</span>
                            )}
                            {over40hWeek && !isEmpty && (
                              <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-semibold">&gt;40h</span>
                            )}
                            {showMinutes && <span>{formatMinutesToHM(primaryMinutes)}</span>}
                          </span>
                        </div>
                        {showMinutes && (
                          <div className="text-[9px] opacity-80">
                            {planned > 0 ? `P ${formatMinutesToHM(planned)}` : ''}
                            {planned > 0 && total > 0 ? ' • ' : ''}
                            {total > 0 ? `L ${formatMinutesToHM(total)}` : ''}
                            {(planned > 0 || total > 0) && other > 0 ? ' • ' : ''}
                            {other > 0 ? `O ${formatMinutesToHM(other)}` : ''}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      );
    }

    // Tasks month grid
    return (
      <DayGrid
        days={days}
        renderCell={TasksCellContent}
        inMonth={inMonth}
        cellClassName="min-h-[120px]"
        isCellClickable={(ymd) => ((tasksByDay[ymd] || []).length === 0)}
        onCellClick={(ymd) => openAssignForDate(ymd)}
        renderHeaderRight={(ymd) => (
          <button
            type="button"
            className="w-5 h-5 rounded border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
            title={projectId ? 'Assign work' : 'Select a project first'}
            onClick={(e) => openAssignForDate(ymd, e)}
          >
              <span
                className={isRedFocus ? 'font-bold' : undefined}
                style={isRedFocus ? { color: warmTheme?.primary || '#fb7185' } : undefined}
              >+
              </span>
          </button>
        )}
      />
    );
  }

  function TimelineHeader({ days, labelWidthPx = LABEL_COL_PX, dayWidthPx = DAY_COL_PX }) {
    const tYmd = toYMD(today);
    const todayIndex = days.indexOf(tYmd);
    return (
      <div className="grid" style={{ gridTemplateColumns: `${labelWidthPx}px repeat(${days.length}, ${dayWidthPx}px)` }}>
        <div className="sticky left-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800" />
        {days.map(ymd => {
          const d = ymdToDate(ymd);
          const isWknd = d ? (d.getDay() === 0 || d.getDay() === 6) : false;
          return (
            <div key={`hdr-${ymd}`} className={`text-[11px] px-2 py-1 border-b border-gray-200 dark:border-gray-800 ${isWknd ? 'bg-gray-50 dark:bg-gray-800' : ''}`}>
              {formatDayLabel(ymd)}
            </div>
          );
        })}
        {todayIndex >= 0 && (
          <div
            className="pointer-events-none"
            style={{
              gridColumn: `2 / span ${days.length}`,
              position: 'relative',
              height: 0,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: -9999,
                bottom: -9999,
                left: `${(todayIndex + 0.5) * dayWidthPx}px`,
                width: 2,
                background: '#EF4444',
                opacity: 0.6,
              }}
            />
          </div>
        )}
      </div>
    );
  }

  // Shared grid scaffolding: render a 7-column day grid (used by Month People and Week view)
  function DayGrid({
    days,
    renderCell,
    inMonth,
    cellClassName,
    renderHeaderRight,
    isCellClickable,
    onCellClick,
  }) {
    return (
      <div
        className="grid grid-cols-7 gap-px bg-gray-300 dark:bg-gray-700"
        style={isRed2 ? { background: theme.surface2 || '#fff1f2' } : undefined}
      >
        {days.map((cell, idx) => (
          <div
            key={idx}
            className={`${cellClassName || 'min-h-[72px]'} bg-white dark:bg-gray-900 p-2 ${(() => { const d = ymdToDate(cell.ymd); const g = d?.getDay(); return (g === 0 || g === 6) ? 'bg-gray-50 dark:bg-gray-800' : ''; })()} ${isCellClickable && isCellClickable(cell.ymd) ? 'cursor-pointer' : ''}`}
            onClick={(isCellClickable && isCellClickable(cell.ymd)) ? (() => onCellClick && onCellClick(cell.ymd)) : undefined}
            style={isRed2 ? { background: theme.surface2 || '#fff1f2' } : undefined}
          >
            <div className="flex items-start justify-between gap-2">
              <div className={`text-xs ${inMonth ? (inMonth(cell.date) ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500') : 'text-gray-800 dark:text-gray-200'}`}>{formatDayLabel(cell.ymd)}</div>
              {renderHeaderRight ? renderHeaderRight(cell.ymd) : null}
            </div>
            <div className="mt-1 space-y-1">
              {renderCell(cell.ymd)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  function PeopleCellContent(ymd, options = null) {
    if (!(displaySelectedEmpIds.length > 0)) return null;

    const weekStartYmd = options?.weekStartYmd || null;
    const weekTotalsByUid = options?.weekTotalsByUid || null;

    return (
      <div className="mt-1 space-y-1">
        {displaySelectedEmpIds.map(uid => {
          const info = getPeopleDayInfo(ymd, uid);
          const planned = info.planned;
          const loggedAssigned = info.loggedAssigned;
          const other = info.other;
          const total = info.total;
          const over8h = !!info.over8h;
          const emp = sanitizedVisibleEmployees.find(e => String(e.id) === String(uid));
          const baseColor = getPersonColor(emp?.display_name);
          const isEmpty = planned <= 0 && total <= 0;
          const bg = isEmpty ? null : baseColor;
          const text = isEmpty ? null : '#111827';
          const showMinutes = info.showMinutes;
          const primaryMinutes = info.primaryMinutes;

          const isWeekStartCell = !!weekStartYmd && ymd === weekStartYmd;
          const weekTotal = isWeekStartCell ? Number((weekTotalsByUid && weekTotalsByUid[String(uid)]) || 0) : 0;
          const over40hWeek = isWeekStartCell && weekTotal > WEEK_LIMIT_MINUTES;
          return (
            <button
              key={`${ymd}-${uid}`}
              type="button"
              className={`w-full text-[10px] px-2 py-1 rounded text-left ${isEmpty ? 'bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-300/80 dark:hover:bg-gray-700/80' : `hover:brightness-110 ${over8h ? 'ring-1 ring-red-600' : ''}`}`}
              style={isEmpty ? undefined : { background: bg, color: text }}
              title={showMinutes
                ? `${emp?.display_name || 'User'} • Planned ${formatMinutesToHM(planned)} • Logged ${formatMinutesToHM(total)} (P ${formatMinutesToHM(loggedAssigned)} | O ${formatMinutesToHM(other)})`
                : `${emp?.display_name || 'User'}`}
              onClick={() => setDayDetails({ date: ymd, user_id: uid })}
            >
              <div className="flex items-center justify-between">
                <span className="truncate max-w-[70%]">{emp?.display_name || 'User'}</span>
                <span className="flex items-center gap-1">
                  {over8h && !isEmpty && (
                    <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-semibold">&gt;8h</span>
                  )}
                  {over40hWeek && !isEmpty && (
                    <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700 text-[9px] font-semibold">&gt;40h</span>
                  )}
                  {showMinutes && <span>{formatMinutesToHM(primaryMinutes)}</span>}
                </span>
              </div>
              {showMinutes && (
                <div className="text-[9px] opacity-80">
                  {planned > 0 ? `P ${formatMinutesToHM(planned)}` : ''}
                  {planned > 0 && total > 0 ? ' • ' : ''}
                  {total > 0 ? `L ${formatMinutesToHM(total)}` : ''}
                  {(planned > 0 || total > 0) && other > 0 ? ' • ' : ''}
                  {other > 0 ? `O ${formatMinutesToHM(other)}` : ''}
                </div>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  function ProjectsCellContent(ymd) {
    // Show compact project presence per day (top 2 + overflow)
    const pool = projectId ? projectOptions.filter(p => String(p.id) === String(projectId)) : projectOptions;
    const overlapsProject = (p) => {
      if (!p?.start_date || !p?.end_date) return false;
      return p.start_date <= ymd && p.end_date >= ymd;
    };
    const dayProjects = pool.filter(overlapsProject);
    const topTwo = dayProjects.slice(0, 2);
    const more = Math.max(0, dayProjects.length - topTwo.length);
    return (
      <>
        {topTwo.map(p => (
          <button
            key={`${ymd}-${p.id}`}
            title={`${p.name} • ${p.client_name}`}
            onClick={() => nav(`/projects/${p.id}`)}
            className="w-full h-1.5 rounded hover:brightness-110"
            style={{ background: getProjectColorForId(p.id) }}
          />
        ))}
        {more > 0 && (
          <div className="text-[10px] text-gray-500">+{more} more</div>
        )}
      </>
    );
  }

  function TasksCellContent(ymd) {
    const list = tasksByDay[ymd] || [];
    if (!list.length) return null;

    return (
      <div className="space-y-1">
        {list.map(a => {
          const bg = getProjectColorForId(a.project_id);
          const showProjectPrefix = !projectId;
          const sliceMinutes = (a && a._slice_minutes != null) ? Number(a._slice_minutes || 0) : null;
          const assList = Array.isArray(a?.assignees) ? a.assignees : [];
          const isUnassigned = assList.length === 0;
          const creatorLabel = String(a?.created_by_name || '').trim();
          const effectiveStartYmd = a.start_date || a.due_date || null;
          const titleParts = [
            a.project_name,
            a.job_name,
            a.task_name ? `› ${a.task_name}` : null,
            isUnassigned ? (creatorLabel || 'Unassigned') : assigneeSummary(a),
            effectiveStartYmd ? `Start ${formatDateDMY(effectiveStartYmd)} • Due ${formatDateDMY(a.due_date)}` : `Due ${formatDateDMY(a.due_date)}`,
            sliceMinutes != null ? `Planned ${formatMinutesToHM(sliceMinutes)}` : null,
          ].filter(Boolean);

          return (
            <div key={`task-${a.id}-${a._slice_date || a.due_date}`} className="relative">
              <button
                type="button"
                className="w-full text-left rounded px-2 py-1 hover:brightness-110"
                style={{ background: bg, color: 'white', boxShadow: '0 1px 0 rgba(0,0,0,0.25)' }}
                title={titleParts.join(' • ')}
                onClick={() => openTaskDetailsForTask(a)}
              >
                <div className="text-[11px] font-semibold truncate" style={{ textShadow: '0 1px 1px rgba(0,0,0,0.35)' }}>
                  {showProjectPrefix ? `${a.project_name || 'Project'} • ` : ''}{a.job_name || '—'}{a.task_name ? ` › ${a.task_name}` : ''}
                </div>
                <div className="text-[10px] opacity-90 truncate" style={{ textShadow: '0 1px 1px rgba(0,0,0,0.35)' }}>
                  {isUnassigned ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-white/20 text-white text-[10px] font-semibold">
                      {creatorLabel || 'Unassigned'}
                    </span>
                  ) : (
                    assigneeSummary(a)
                  )}
                  {sliceMinutes != null ? ` • ${formatMinutesToHM(sliceMinutes)}` : ''}
                </div>
              </button>
            </div>
          );
        })}
      </div>
    );
  }

  function WeekView() {
    // Build Mon..Sun days from computed range
    const start = ymdToDate(fromYmd);
    const end = ymdToDate(toYmd);
    if (!start || !end) return <div className="text-xs text-gray-600 dark:text-gray-400">Loading…</div>;
    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const ymd = toYMD(d);
      days.push({ date: new Date(d), ymd });
    }
    if (subtab === 'people') {
      const weekStartYmd = days[0]?.ymd || null;
      const weekTotalsByUid = {};
      if (weekStartYmd) {
        for (const uid of (Array.isArray(displaySelectedEmpIds) ? displaySelectedEmpIds : [])) {
          weekTotalsByUid[String(uid)] = getWeekTotalMinutesForUser(weekStartYmd, uid);
        }
      }

      return (
        <div className="space-y-2">
          {displaySelectedEmpIds.length > 0 && weekStartYmd && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">Week totals</span>
              {displaySelectedEmpIds.map(uid => {
                const emp = sanitizedVisibleEmployees.find(e => String(e.id) === String(uid));
                const totalMins = Number(weekTotalsByUid[String(uid)] || 0);
                const over40 = totalMins > WEEK_LIMIT_MINUTES;
                const color = getPersonColor(emp?.display_name);
                return (
                  <span
                    key={`wk-total-${uid}`}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded border ${over40 ? 'border-red-300 bg-red-50 text-red-800' : 'border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/40'}`}
                  >
                    <span className="inline-block w-2.5 h-2.5 rounded" style={{ background: color }} />
                    <span className="truncate max-w-[140px]">{emp?.display_name || 'User'}</span>
                    <span className="tabular-nums">{formatMinutesToHM(totalMins)}</span>
                    {over40 && <span className="font-semibold">&gt;40h</span>}
                  </span>
                );
              })}
            </div>
          )}
          <DayGrid
            days={days}
            renderCell={(ymd) => PeopleCellContent(ymd, { weekStartYmd, weekTotalsByUid })}
            inMonth={null}
          />
        </div>
      );
    }
    if (subtab === 'tasks') {
      return (
        <DayGrid
          days={days}
          renderCell={TasksCellContent}
          inMonth={null}
          cellClassName="min-h-[120px]"
          isCellClickable={(ymd) => ((tasksByDay[ymd] || []).length === 0)}
          onCellClick={(ymd) => openAssignForDate(ymd)}
          renderHeaderRight={(ymd) => (
            <button
              type="button"
              className="w-5 h-5 rounded border border-gray-200 dark:border-gray-700 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center"
              title={projectId ? 'Assign work' : 'Select a project first'}
              onClick={(e) => openAssignForDate(ymd, e)}
            >
              <span
                className={isRedFocus ? 'font-bold' : undefined}
                style={isRedFocus ? { color: warmTheme?.primary || '#fb7185' } : undefined}
              >+
              </span>
            </button>
          )}
        />
      );
    }
    return <DayGrid days={days} renderCell={ProjectsCellContent} inMonth={null} />;
  }
  function TimelineProjects() {
    const start = ymdToDate(fromYmd);
    const end = ymdToDate(toYmd);
    if (!start || !end) return null;

    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(toYMD(d));
    }

    const idx = (ymd) => days.indexOf(ymd);

    const weekendBands = days
      .map((ymd, i) => ({ ymd, i }))
      .filter(x => isWeekendYmd(x.ymd))
      .map(x => ({
        key: `wknd-${x.ymd}`,
        leftPx: x.i * DAY_COL_PX,
        widthPx: DAY_COL_PX,
      }));

    function clipBar(s, e) {
      if (!s || !e) return null;
      const cs = s < fromYmd ? fromYmd : s;
      const ce = e > toYmd ? toYmd : e;
      const si = idx(cs);
      const ei = idx(ce);
      if (si < 0 || ei < 0 || si > ei) return null;
      const leftPx = si * DAY_COL_PX;
      const widthPx = Math.max(8, (ei - si + 1) * DAY_COL_PX);
      return { leftPx, widthPx };
    }

    const pool = projectId ? projectOptions.filter(p => String(p.id) === String(projectId)) : projectOptions;

    const projectRows = pool
      .map(p => ({ p, clip: clipBar(p.start_date, p.end_date) }))
      .filter(r => r.clip);

    const assignmentsByProject = new Map();
    (Array.isArray(filteredAssignments) ? filteredAssignments : []).forEach(a => {
      if (projectId && String(a.project_id) !== String(projectId)) return;
      const clip = clipBar(a.start_date || fromYmd, a.due_date);
      if (!clip) return;
      if (!assignmentsByProject.has(a.project_id)) assignmentsByProject.set(a.project_id, []);
      assignmentsByProject.get(a.project_id).push({ a, clip });
    });

    const assigneeLabels = (a) => {
      const list = Array.isArray(a?.assignees) ? a.assignees : [];
      return list
        .map(x => String(x?.display_name || '').trim())
        .filter(Boolean);
    };

    const assigneeSummary = (a) => {
      const labels = assigneeLabels(a);
      if (!labels.length) return String(a?.created_by_name || '').trim() || 'Unassigned';
      const shown = labels.slice(0, 2);
      const more = Math.max(0, labels.length - shown.length);
      return more > 0 ? `${shown.join(', ')} +${more}` : shown.join(', ');
    };

    return (
      <div className="overflow-x-auto">
        <div className="min-w-max">
          <TimelineHeader days={days} labelWidthPx={LABEL_COL_PX} dayWidthPx={DAY_COL_PX} />
          <div className="mt-2 space-y-3">
            <div>
              <div className="text-sm font-medium mb-1">Projects</div>
              <div className="space-y-2">
                {projectRows.map(({ p, clip }) => (
                  <div key={p.id} className="grid" style={{ gridTemplateColumns: `${LABEL_COL_PX}px repeat(${days.length}, ${DAY_COL_PX}px)` }}>
                    <div className="sticky left-0 z-10 bg-white dark:bg-gray-900 text-sm truncate pr-2 px-2 py-1 border-b border-gray-100 dark:border-gray-800">{p.name}</div>
                    <div className="relative h-3 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden my-1" style={{ gridColumn: `2 / span ${days.length}` }}>
                      {weekendBands.map(b => (
                        <div
                          key={`${p.id}-${b.key}`}
                          className="absolute top-0 bottom-0"
                          style={{ left: `${b.leftPx}px`, width: `${b.widthPx}px`, background: 'rgba(0,0,0,0.06)' }}
                        />
                      ))}
                      <button
                        type="button"
                        title={`${p.name} • ${p.client_name}`}
                        onClick={() => nav(`/projects/${p.id}`)}
                        className="absolute h-3 rounded hover:brightness-110"
                        style={{ left: `${clip.leftPx}px`, width: `${clip.widthPx}px`, background: getProjectColorForId(p.id) }}
                      />
                    </div>
                  </div>
                ))}
                {projectRows.length === 0 && <div className="text-xs text-gray-600 dark:text-gray-400">No projects in range</div>}
              </div>
            </div>

            <div>
              <div className="text-sm font-medium mb-1">Assignments</div>
              {[...assignmentsByProject.entries()].map(([pid, rows]) => {
                const proj = projectOptions.find(x => String(x.id) === String(pid));
                const label = proj ? proj.name : `Project ${pid}`;
                return (
                  <div key={pid} className="mb-2">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">{label}</div>
                    <div className="space-y-1">
                      {rows.map(({ a, clip }) => (
                        <div key={a.id} className="grid" style={{ gridTemplateColumns: `${LABEL_COL_PX}px repeat(${days.length}, ${DAY_COL_PX}px)` }}>
                          <div className="sticky left-0 z-10 bg-white dark:bg-gray-900 text-[11px] pr-2 px-2 py-1 border-b border-gray-100 dark:border-gray-800">
                            <div className="truncate">{a.job_name}{a.task_name ? ` › ${a.task_name}` : ''}</div>
                            {(() => {
                              const labels = assigneeLabels(a);
                              if (labels.length === 0) {
                                const creatorLabel = String(a?.created_by_name || '').trim();
                                return (
                                  <div className="mt-0.5">
                                    <Chip label={creatorLabel || 'Unassigned'} className="text-[10px] px-1.5 py-0.5" />
                                  </div>
                                );
                              }
                              return (
                                <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                                  {labels.slice(0, 2).map((label, idx) => (
                                    <Chip key={`${a.id}-asg-${idx}`} label={label} className="text-[10px] px-1.5 py-0.5" />
                                  ))}
                                  {labels.length > 2 && (
                                    <Chip label={`+${labels.length - 2}`} className="text-[10px] px-1.5 py-0.5" />
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          <div className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden my-2" style={{ gridColumn: `2 / span ${days.length}` }}>
                            {weekendBands.map(b => (
                              <div
                                key={`${a.id}-${b.key}`}
                                className="absolute top-0 bottom-0"
                                style={{ left: `${b.leftPx}px`, width: `${b.widthPx}px`, background: 'rgba(0,0,0,0.06)' }}
                              />
                            ))}
                            <div
                              className="absolute h-2 rounded"
                              style={{ left: `${clip.leftPx}px`, width: `${clip.widthPx}px`, background: getProjectColorForId(pid) }}
                              title={`${a.project_name} • ${a.job_name}${a.task_name ? ` › ${a.task_name}` : ''} • ${assigneeSummary(a)} • ${formatDateDMY(a.start_date || a.due_date || fromYmd)} → ${formatDateDMY(a.due_date)}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {assignmentsByProject.size === 0 && <div className="text-xs text-gray-600 dark:text-gray-400">No assignments in range</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function TimelinePeople() {
    const start = ymdToDate(fromYmd);
    const end = ymdToDate(toYmd);
    if (!start || !end) return null;

    const days = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(toYMD(d));
    }

    if (!displaySelectedEmpIds.length) {
      return <div className="text-xs text-gray-600 dark:text-gray-400">Select people above to view the timeline.</div>;
    }

    return (
      <div className="overflow-x-auto">
        <div className="min-w-max">
          <TimelineHeader days={days} labelWidthPx={LABEL_COL_PX} dayWidthPx={DAY_COL_PX} />
          <div className="grid mt-0" style={{ gridTemplateColumns: `${LABEL_COL_PX}px repeat(${days.length}, ${DAY_COL_PX}px)` }}>
            {displaySelectedEmpIds.map(uid => {
              const emp = sanitizedVisibleEmployees.find(e => String(e.id) === String(uid));
              const color = getPersonColor(emp?.display_name);
              return (
                <React.Fragment key={`row-${uid}`}>
                  <div className="sticky left-0 z-10 bg-white dark:bg-gray-900 text-xs px-2 py-2 border-t border-gray-200 dark:border-gray-800 flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded" style={{ background: color }} />
                    <span className="truncate">{emp?.display_name || 'User'}</span>
                  </div>
                  {days.map(ymd => {
                    const info = getPeopleDayInfo(ymd, uid);
                    const over = !!info.over8h;
                    const total = Number(info.total || 0);
                    const planned = Number((plannedByDayUser[ymd] || {})[String(uid)] || 0);
                    const loggedAssigned = Number(info.loggedAssigned || 0);
                    const other = Number(info.other || 0);
                    const isWeekend = (() => { const d = ymdToDate(ymd); const g = d?.getDay(); return g === 0 || g === 6; })();
                    return (
                      <div
                        key={`cell-${uid}-${ymd}`}
                        className={`text-[11px] px-2 py-2 border-t border-gray-200 dark:border-gray-800 ${isWeekend ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
                        title={`Planned ${formatMinutesToHM(planned)} • Logged ${formatMinutesToHM(total)}${total>0||other>0?` (P ${formatMinutesToHM(loggedAssigned)} | O ${formatMinutesToHM(other)})`:''}`}
                        style={{ borderLeftWidth: 4, borderLeftColor: over ? '#B91C1C' : color }}
                      >
                        {(total > 0 || planned > 0 || other > 0) ? (
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900 dark:text-gray-100">{formatMinutesToHM(total > 0 ? total : planned)}</span>
                            {planned > 0 && <span className="opacity-80 text-gray-700 dark:text-gray-300">P {formatMinutesToHM(planned)}</span>}
                          </div>
                        ) : (
                          <div className="h-4" />
                        )}
                        {other > 0 && (
                          <div className="opacity-80 text-gray-700 dark:text-gray-300">O {formatMinutesToHM(other)}</div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  function DayDetailsDrawer() {
    const dd = dayDetails;
    const emp = sanitizedVisibleEmployees.find(e => String(e.id) === String(dd?.user_id));
    const selectedProjectObj = projectId ? projectOptions.find(p => String(p.id) === String(projectId)) : null;
    const viewerRole = String(me?.role || '').trim().toLowerCase();
    const isManagerAdmin = viewerRole === 'manager' || viewerRole === 'admin';
    const isEmployee = viewerRole === 'employee';
    const isDrawerOwner = me?.id != null && dd?.user_id != null && String(me.id) === String(dd.user_id);

    const canEditAssignment = isManagerAdmin || (isEmployee && isDrawerOwner);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [drawerError, setDrawerError] = useState('');
    const [planned, setPlanned] = useState([]);
    const [logged, setLogged] = useState([]);
    const [totals, setTotals] = useState({ assigned_minutes: 0, other_minutes: 0, total_minutes: 0, planned_minutes: 0 });

    const loggedMinutesByAssignmentId = useMemo(() => {
      const m = new Map();
      (Array.isArray(logged) ? logged : []).forEach(e => {
        const aid = e?.assignment?.id || e?.assignment_id || null;
        if (!aid) return;
        const mins = Number(e?.minutes || e?.minutes_logged || 0);
        if (!Number.isFinite(mins) || mins <= 0) return;
        const k = String(aid);
        m.set(k, (m.get(k) || 0) + mins);
      });
      return m;
    }, [logged]);

    useEffect(() => {
      let cancelled = false;
      (async () => {
        if (!dd) return;
        setDrawerLoading(true);
        setDrawerError('');
        try {
          const res = await fetchDayDetails(dd.user_id, dd.date, {
            projectId: projectId || null,
            managerFilter,
          });
          if (!cancelled) {
            setPlanned(res?.planned_assignments || res?.planned || []);
            setLogged(res?.logged_entries || res?.entries || []);
            setTotals(res?.totals || { assigned_minutes: 0, other_minutes: 0, total_minutes: 0, planned_minutes: 0 });
          }
        } catch (err) {
          if (!cancelled) setDrawerError(err?.message || 'Failed to load day details');
        } finally {
          if (!cancelled) setDrawerLoading(false);
        }
      })();
      return () => { cancelled = true; };
    }, [dd?.user_id, dd?.date, projectId, managerFilter, drawerReloadKey]);

    if (!dd) return null;

    return (
      <div className="fixed inset-0 z-40">
        <div onClick={() => setDayDetails(null)} className="absolute inset-0 bg-black/30 backdrop-blur-[6px]" />
        <div className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-white dark:bg-gray-900 shadow-xl flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div>
              <div className="text-lg font-semibold">{emp?.display_name || 'User'}</div>
              <div className="text-xs text-gray-600 dark:text-gray-400">{dd.date}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={`px-3 py-1 rounded text-sm text-white ${isRedFocus ? 'hover:brightness-95' : 'bg-blue-600 hover:bg-blue-700'}`}
                style={isRedFocus ? { background: warmTheme?.primary || '#fb7185' } : undefined}
                title={selectedProjectObj ? 'Assign work' : 'Assign work (select project in modal)'}
                onClick={() => {
                  try {
                    setWorkMode('create');
                    setWorkInitialAssignment(null);

                    if (selectedProjectObj) {
                      setWorkProject({
                        id: selectedProjectObj.id,
                        name: selectedProjectObj.name,
                        client_name: selectedProjectObj.client_name,
                        client_id: selectedProjectObj.client_id != null ? selectedProjectObj.client_id : null,
                        client_color: selectedProjectObj.client_color || null,
                      });
                    } else {
                      // Global mode: allow selecting client + project inside modal.
                      setWorkProject(null);
                    }

                    setWorkInitialAssigneeIds([dd.user_id]);
                    setWorkPrefill({
                      jobId: null,
                      taskId: null,
                      plannedHours: null,
                      startDate: dd.date,
                      dueDate: dd.date,
                    });
                    setDayDetails(null);
                  } catch (err) {
                    console.error('Assign Work (drawer) error', err);
                    toast.error('Failed to open Assign Work');
                  }
                }}
              >
                Assign Work
              </button>
              <button onClick={() => setDayDetails(null)} className="rounded p-2 hover:bg-gray-100 dark:hover:bg-gray-800">✕</button>
            </div>
          </div>
          <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-56px)]">
            {drawerLoading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>}
            {drawerError && <div className="text-sm text-red-600">{drawerError}</div>}
            {!drawerLoading && !drawerError && (
              <>
                <div className="text-sm">
                  Planned {formatMinutesToHM(totals.planned_minutes || 0)} • Logged {formatMinutesToHM(totals.total_minutes || 0)} — P {formatMinutesToHM(totals.assigned_minutes || 0)} • O {formatMinutesToHM(totals.other_minutes || 0)}
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-300">Planned</div>
                  {planned.map(p => {
                    const assignmentId = p?.assignment_id || null;
                    const loggedMins = Number(loggedMinutesByAssignmentId.get(String(assignmentId || '')) || 0);
                    const canRemoveDay = !!assignmentId && canEditAssignment && loggedMins <= 0;
                    const canShowDeleteTask = isManagerAdmin && !!assignmentId;
                    const deleteDayLabel = isManagerAdmin ? 'Remove day' : 'Delete day';
                    const plannedClientNameRaw = String(p?.project?.client_name ?? '').trim();
                    const plannedClientName = plannedClientNameRaw ? plannedClientNameRaw : 'No client';

                    const openEdit = () => {
                      if (!assignmentId) return;
                      setWorkMode('edit');
                      setWorkInitialAssignment(null);
                      setWorkEditAssignmentId(assignmentId);
                      setWorkProject(null);
                      setWorkInitialAssigneeIds([]);
                      setWorkPrefill(null);
                    };

                    return (
                      <div
                        key={`planned-${assignmentId || `${p.project_id || 'p'}-${p.job_id || 'j'}-${p.task_id || 't'}-${p.due_date || 'd'}`}`}
                        className="w-full text-left rounded border border-gray-200 dark:border-gray-700 p-2 text-sm"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium whitespace-normal break-words">{p.project?.name || '—'}</div>
                            <div className="text-[11px] text-gray-600 dark:text-gray-400 whitespace-normal break-words">
                              <span className="text-gray-500 dark:text-gray-500">Client:</span>{' '}
                              <span>{plannedClientName}</span>
                            </div>
                            <div className="text-gray-500 dark:text-gray-400 whitespace-normal break-words">
                              {p.job ? p.job.name : ''}{p.task ? ` › ${p.task.name}` : ''}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 shrink-0">
                            <div className="text-xs whitespace-nowrap">{formatMinutesToHM(p.planned_minutes || 0)}</div>

                            {canEditAssignment && assignmentId && (
                              <button
                                type="button"
                                className="px-2 py-1.5 rounded border border-blue-600 text-blue-700 hover:bg-blue-50 text-xs font-medium dark:border-blue-500 dark:text-blue-300 dark:hover:bg-blue-900/20"
                                title="Edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openEdit();
                                }}
                              >
                                Edit
                              </button>
                            )}

                            {canRemoveDay && (
                              <button
                                type="button"
                                className="px-2 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 text-xs font-medium dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-900/20"
                                title={deleteDayLabel}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openRemoveDayConfirm({ taskId: assignmentId, ymd: dd.date, userId: isManagerAdmin ? dd.user_id : null });
                                }}
                              >
                                {deleteDayLabel}
                              </button>
                            )}

                            {canShowDeleteTask && (
                              <button
                                type="button"
                                className="px-2 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 text-xs font-medium dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-900/20"
                                title="Delete task"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteConfirm({ ...p, id: assignmentId });
                                }}
                              >
                                Delete task
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-1">
                          <div className="text-[11px] text-gray-600 dark:text-gray-400">{formatStatusLabel(p.status) || 'planned'}</div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400">{loggedMins > 0 ? `Logged ${formatMinutesToHM(loggedMins)}` : ''}</div>
                        </div>
                      </div>
                    );
                  })}
                  {planned.length === 0 && <div className="text-xs text-gray-600 dark:text-gray-400">No planned work</div>}

                  <div className="text-xs font-semibold text-gray-700 dark:text-gray-300 mt-3">Logged</div>
                  {logged.map(it => (
                    <button
                      key={`logged-${it.id}`}
                      type="button"
                      className="w-full text-left rounded border border-gray-200 dark:border-gray-700 p-2 text-sm hover:brightness-110"
                      onClick={() => {
                        if (!it?.id) return;
                        setTimeEntryDetailsId(it.id);
                        setTimeEntryDetailsOpen(true);
                      }}
                      title="View time entry"
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium whitespace-normal break-words">{it.project?.name || '—'}</div>
                          <div className="text-[11px] text-gray-600 dark:text-gray-400 whitespace-normal break-words">
                            <span className="text-gray-500 dark:text-gray-500">Client:</span>{' '}
                            <span>{String(it?.project?.client_name ?? '').trim() || 'No client'}</span>
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 whitespace-normal break-words">
                            {it.job ? it.job.name : ''}{it.task ? ` › ${it.task.name}` : ''}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <div className="text-xs whitespace-nowrap">{formatMinutesToHM(it.minutes)}</div>

                          {isManagerAdmin && it?.id && it?.is_system && String(it?.source || '') === 'AUTO_FINALIZE' && (
                            <>
                              <button
                                type="button"
                                className="px-2 py-1.5 rounded border border-blue-600 text-blue-700 hover:bg-blue-50 text-xs font-medium dark:border-blue-500 dark:text-blue-300 dark:hover:bg-blue-900/20"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTimeEntryDetailsId(it.id);
                                  setTimeEntryDetailsOpen(true);
                                }}
                                title="Edit auto-finalized entry"
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="px-2 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 text-xs font-medium dark:border-red-900/50 dark:text-red-300 dark:hover:bg-red-900/20"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const ok = window.confirm('Delete this auto-finalized time entry?');
                                  if (!ok) return;
                                  try {
                                    await deleteTimeEntry(it.id);
                                    toast.success('Time entry deleted');
                                    setDrawerReloadKey(k => k + 1);
                                    setRefreshKey(k => k + 1);
                                  } catch (err) {
                                    toast.error(err?.message || 'Failed to delete time entry');
                                  }
                                }}
                                title="Delete auto-finalized entry"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-[11px] text-gray-600 dark:text-gray-400 mt-1">{it.description || (it.is_other ? 'Other work' : (it.assignment ? (formatStatusLabel(it.assignment.status) || 'planned') : 'Planned'))}</div>
                    </button>
                  ))}
                  {logged.length === 0 && <div className="text-xs text-gray-600 dark:text-gray-400">No logged entries</div>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  function PeopleChips() {
    if (subtab !== 'people') return null;
    return (
      <div className="mb-2">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
            onClick={() => {
              setHiddenEmpIds([]);
              setPeopleFilterInitialized(true);
            }}
            disabled={sanitizedVisibleEmployees.length === 0}
            title="Select all visible people"
          >
            Select all
          </button>
          <button
            type="button"
            className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
            onClick={() => {
              setHiddenEmpIds(sanitizedVisibleEmployees.map(e => String(e.id)));
              setPeopleFilterInitialized(true);
            }}
            disabled={displaySelectedEmpIds.length === 0}
            title="Deselect all"
          >
            Deselect all
          </button>
          {sanitizedVisibleEmployees.map(emp => {
            const active = displaySelectedEmpIds.includes(emp.id);
            const pinned = isPinnedManager(emp.display_name);
            const color = active ? getPersonColor(emp.display_name) : '#9CA3AF';
            return (
              <button
                key={emp.id}
                type="button"
                onClick={() => {
                  setHiddenEmpIds(prev => {
                    const set = new Set((Array.isArray(prev) ? prev : []).map(String));
                    const id = String(emp.id);
                    if (active) set.add(id);
                    else set.delete(id);
                    return Array.from(set);
                  });
                  setPeopleFilterInitialized(true);
                }}
                className={pinned
                  ? 'px-2 py-1 text-xs rounded border border-blue-200 dark:border-blue-800 border-l-4 border-l-blue-500 dark:border-l-blue-400 bg-blue-50 dark:bg-blue-900/20 text-gray-900 dark:text-gray-100 hover:bg-blue-100/70 dark:hover:bg-blue-900/30'
                  : 'px-2 py-1 text-xs text-white hover:brightness-110'
                }
                style={pinned
                  ? undefined
                  : { background: color, clipPath: 'polygon(30% 5%, 70% 5%, 85% 15%, 95% 30%, 95% 70%, 85% 85%, 70% 95%, 30% 95%, 15% 85%, 5% 70%, 5% 30%, 15% 15%)' }
                }
                title={pinned ? `${emp.display_name} (pinned)` : emp.display_name}
              >
                <span className={pinned ? 'font-semibold' : undefined}>{emp.display_name}</span>
              </button>
            );
          })}
          <span className="text-[11px] text-gray-600 dark:text-gray-400">Selected: {displaySelectedEmpIds.length}</span>
        </div>
      </div>
    );
  }

  function Header() {
    const quickLabel = view === 'month' ? {
      last: 'Last month',
      this: 'This month',
      next: 'Next month',
    } : {
      last: 'Last week',
      this: 'This week',
      next: 'Next week',
    };

    const prevLabel = view === 'month' ? 'Previous Month' : 'Previous Week';
    const nextLabel = view === 'month' ? 'Next Month' : 'Next Week';

    const selectedProjectObj = projectId ? projectOptions.find(p => String(p.id) === String(projectId)) : null;
    const selectedPeopleCount = displaySelectedEmpIds.length;
    const selectedPersonId = selectedPeopleCount === 1 ? displaySelectedEmpIds[0] : null;
    const selectedPersonName = selectedPersonId
      ? (sanitizedVisibleEmployees.find(e => String(e.id) === String(selectedPersonId))?.display_name || null)
      : null;

    return (
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <div
            role="tablist"
            aria-label="Calendar view"
            className="inline-flex items-center overflow-hidden rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-sm"
          >
            <button
              type="button"
              role="tab"
              aria-selected={view === 'month'}
              onClick={() => setView('month')}
              className={`px-3 py-1.5 text-sm font-medium border-r border-gray-300 dark:border-gray-600 ${view === 'month'
                ? (isRed2 ? 'text-white font-semibold' : 'bg-gray-900 text-white font-semibold')
                : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              style={(view === 'month' && isRed2) ? { background: theme.primary || '#e11d48' } : undefined}
            >
              Month
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'week'}
              onClick={() => setView('week')}
              className={`px-3 py-1.5 text-sm font-medium ${view === 'week'
                ? (isRed2 ? 'text-white font-semibold' : 'bg-gray-900 text-white font-semibold')
                : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
              style={(view === 'week' && isRed2) ? { background: theme.primary || '#e11d48' } : undefined}
            >
              Week
            </button>
          </div>
          <SegmentedControl
            ariaLabel="Calendar subtab"
            options={[{ key: 'tasks', label: 'Tasks' }, { key: 'people', label: 'People' }]}
            value={subtab}
            onChange={setSubtab}
            size="sm"
          />

          <button
            type="button"
            className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
            onClick={() => stepRange(-1)}
            aria-label={prevLabel}
            title={prevLabel}
          >
            {prevLabel}
          </button>
          <button
            type="button"
            className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
            onClick={() => stepRange(1)}
            aria-label={nextLabel}
            title={nextLabel}
          >
            {nextLabel}
          </button>

          <div className="ml-2 flex items-center gap-1">
            <button
              type="button"
              className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
              onClick={() => applyQuickRange('this')}
            >
              {quickLabel.this}
            </button>
          </div>

          <div className="ml-2 flex items-center gap-2 text-xs">
            <label className="flex items-center gap-1">
              <span className="text-gray-600 dark:text-gray-400">From</span>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800" />
            </label>
            <label className="flex items-center gap-1">
              <span className="text-gray-600 dark:text-gray-400">To</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800" />
            </label>
            {(customFrom || customTo) && (
              <button
                type="button"
                className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
                onClick={() => { setCustomFrom(''); setCustomTo(''); }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="month"
            value={`${baseDate.getFullYear()}-${String(baseDate.getMonth() + 1).padStart(2, '0')}`}
            onChange={e => {
              const v = String(e.target.value || '');
              const [yy, mm] = v.split('-');
              const y = Number(yy);
              const m = Number(mm);
              if (!y || !m) return;
              setCustomFrom('');
              setCustomTo('');
              setBaseDate(new Date(y, m - 1, 1));
            }}
            className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
            title="Jump to month"
          />

          <select
            value={managerFilter}
            onChange={e => setManagerFilter(e.target.value)}
            disabled={!!fixedProjectId}
            className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
            title="Manager filter"
          >
            <option value="me">My projects</option>
            <option value="all">All managers</option>
            {managers.map(m => (
              <option key={m.id} value={m.id}>{m.display_name || '—'}</option>
            ))}
          </select>

          {!hideProjectFilter && (
            <select
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
              disabled={!!fixedProjectId}
              className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
            >
              <option value="">All projects</option>
              {projectOptions.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select>
          )}

          {enableAssignWorkCta && projectId && selectedProjectObj && (
            <button
              className={`px-3 py-1 rounded text-white text-sm ${isRedFocus ? 'hover:brightness-95' : 'bg-gray-900 hover:bg-gray-800'}`}
              style={isRedFocus ? { background: warmTheme?.primary || '#fb7185' } : undefined}
              onClick={() => {
                try {
                  setWorkProject({ id: selectedProjectObj.id, name: selectedProjectObj.name, client_name: selectedProjectObj.client_name });
                  setWorkInitialAssigneeIds([]);
                } catch (err) {
                  console.error('Assign Work CTA error', err);
                  toast.error('Failed to open Assign Work');
                }
              }}
            >
              Assign Work
            </button>
          )}

          {subtab === 'people' && projectId && selectedProjectObj && (
            <button
              className={`px-3 py-1 rounded text-sm ${selectedPersonId
                ? (isRedFocus ? 'text-white hover:brightness-95' : 'bg-gray-900 text-white hover:bg-gray-800')
                : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
              }`}
              style={(isRedFocus && selectedPersonId) ? { background: warmTheme?.primary || '#fb7185' } : undefined}
              disabled={!selectedPersonId}
              onClick={() => {
                if (!selectedPersonId) {
                  toast.error('Select exactly one person');
                  return;
                }
                try {
                  setWorkProject({ id: selectedProjectObj.id, name: selectedProjectObj.name, client_name: selectedProjectObj.client_name });
                  setWorkInitialAssigneeIds([selectedPersonId]);
                  toast.success(`Assign work for ${selectedPersonName || 'selected person'}`);
                } catch (err) {
                  console.error('Assign Work (People) error', err);
                  toast.error('Failed to open Assign Work');
                }
              }}
              title={selectedPersonId ? `Assign work for ${selectedPersonName || 'selected person'}` : 'Select exactly one person to assign work'}
            >
              Assign Work (Person)
            </button>
          )}

          <button
            type="button"
            className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={loading}
            title="Refresh calendar"
          >
            Refresh
          </button>

          <div className="text-sm text-gray-700 dark:text-gray-300">{windowLabel}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <PeopleChips />
      {loading && <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}
      {!loading && !error && (
        <div className="space-y-3">
          {view === 'month' ? (
            <>
              <MonthLegend />
              <MonthView />
            </>
          ) : (
            <WeekView />
          )}
        </div>
      )}

      <DayDetailsDrawer />

      {timeEntryDetailsOpen && timeEntryDetailsId && (
        <TimeEntryDetailsModal
          open={timeEntryDetailsOpen}
          timeEntryId={timeEntryDetailsId}
          fetcher={fetchTimeEntry}
          viewer={me}
          onChanged={() => setDrawerReloadKey(k => k + 1)}
          onClose={() => { setTimeEntryDetailsOpen(false); setTimeEntryDetailsId(null); }}
        />
      )}

      <AlertDialog
        open={deleteConfirm.open}
        title="Delete entire task?"
        description="This removes all planned days. If there are logged hours, deletion is blocked."
        confirmLabel={deleteConfirm.busy ? 'Deleting…' : 'Delete task'}
        cancelLabel="Cancel"
        busy={deleteConfirm.busy}
        onCancel={closeDeleteConfirm}
        onConfirm={confirmDelete}
      />

      <AlertDialog
        open={removeDayConfirm.open}
        title={removeDayConfirm.userId ? 'Remove this day from the task plan?' : 'Delete this day from the task plan?'}
        description="This is only possible if no hours are logged for this day."
        confirmLabel={removeDayConfirm.busy
          ? (removeDayConfirm.userId ? 'Removing…' : 'Deleting…')
          : (removeDayConfirm.userId ? 'Remove day' : 'Delete day')
        }
        cancelLabel="Cancel"
        busy={removeDayConfirm.busy}
        onCancel={closeRemoveDayConfirm}
        onConfirm={confirmRemoveDay}
      />

      <TaskDetailsModal
        open={taskDetailsOpen}
        assignment={taskDetailsAssignment}
        onClose={() => { setTaskDetailsOpen(false); setTaskDetailsAssignment(null); }}
        onDelete={canManageTasks ? (a) => openDeleteConfirm(a) : null}
        onEdit={(a) => {
          try {
            const id = a?.id;
            if (!id) {
              toast.error('Edit not available');
              return;
            }

            const pid = a?.project_id;
            if (!pid) {
              toast.error('Task has no project');
              return;
            }

            const proj = (Array.isArray(projects) ? projects : []).find(p => String(p.id) === String(pid))
              || (Array.isArray(projectOptions) ? projectOptions : []).find(p => String(p.id) === String(pid));

            const projLabel = proj
              ? { id: proj.id, name: proj.name, client_name: proj.client_name }
              : { id: pid, name: a?.project_name || `Project ${pid}`, client_name: a?.client_name || null };

            const assList = Array.isArray(a?.assignees) ? a.assignees : [];
            const firstAssigneeId = assList.find(x => x && x.user_id != null)?.user_id || null;

            setWorkMode('edit');
            setWorkInitialAssignment({ ...a, assignmentId: id, __prefillSource: 'calendar' });
            setWorkProject(projLabel);
            setWorkInitialAssigneeIds(firstAssigneeId != null ? [firstAssigneeId] : []);
            setWorkPrefill({
              jobId: a?.job_id || null,
              taskId: a?.task_id || null,
              plannedHours: a?.planned_hours ?? null,
              startDate: a?.start_date || a?.due_date || null,
              dueDate: a?.due_date || null,
            });

            setTaskDetailsOpen(false);
            setTaskDetailsAssignment(null);
          } catch (err) {
            console.error('TaskDetailsModal onEdit error', err);
            toast.error('Failed to open edit');
          }
        }}
      />

      {workProject !== undefined && (
        <SharedAddWorkModal
          project={workProject}
          mode={workMode}
          initialAssignment={workInitialAssignment}
          assignmentId={workMode === 'edit' ? (workEditAssignmentId || workInitialAssignment?.assignmentId || workInitialAssignment?.id || null) : null}
          rangeKey={view === 'month' ? 'this_month' : 'this_week'}
          initialAssigneeIds={workInitialAssigneeIds}
          initialStartDate={workPrefill?.startDate}
          initialDueDate={workPrefill?.dueDate}
          initialJobId={workPrefill?.jobId}
          initialTaskId={workPrefill?.taskId}
          initialPlannedHours={workPrefill?.plannedHours}
          onClose={() => { setWorkProject(undefined); setWorkPrefill(null); setWorkInitialAssigneeIds([]); setWorkMode('create'); setWorkInitialAssignment(null); setWorkEditAssignmentId(null); }}
          onCreated={() => {
            setWorkProject(undefined);
            setWorkPrefill(null);
            setWorkInitialAssigneeIds([]);
            setWorkMode('create');
            setWorkInitialAssignment(null);
            setWorkEditAssignmentId(null);
            setRefreshKey(k => k + 1);
            try { bumpRefresh && bumpRefresh(); } catch {}
            try { onWorkCreated && onWorkCreated(); } catch {}
          }}
          onSaved={() => {
            setWorkProject(undefined);
            setWorkPrefill(null);
            setWorkInitialAssigneeIds([]);
            setWorkMode('create');
            setWorkInitialAssignment(null);
            setWorkEditAssignmentId(null);
            setDrawerReloadKey(k => k + 1);
            setRefreshKey(k => k + 1);
            try { bumpRefresh && bumpRefresh(); } catch {}
            try { onWorkCreated && onWorkCreated(); } catch {}
          }}
        />
      )}
    </div>
  );
}
